import { z } from 'zod';

import { authCopy } from '../_shared/copy';
import { passwordField, phoneField, withCodeLength } from '../_shared/schema';

/**
 * Four fields and no fifth: the verification code — the same one-time code, not
 * a second one — the new password, its repeat, and the save action.
 *
 * The match is checked here **and** by the service, which answers
 * `AUTH_PASSWORD_MISMATCH`. The client check exists so the operator is told
 * before they wait for a round trip; it is never the gate, and the disabled
 * submit is a convenience rather than a guard.
 *
 * The error lands on the **confirmation** field, because that is the control
 * the operator has to change — putting it on the first field would ask them to
 * retype the word they meant.
 */
export function buildRecoverSchema(codeLength: number | undefined) {
  return z
    .object({
      code: withCodeLength(codeLength),
      password: passwordField,
      passwordConfirm: z.string(),
    })
    .superRefine((values, context) => {
      if (values.password === values.passwordConfirm) return;

      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['passwordConfirm'],
        message: authCopy.field.passwordMismatch,
      });
    });
}

export interface RecoverFields {
  code: string;
  password: string;
  passwordConfirm: string;
}

/**
 * The identity step's one field.
 *
 * The recovery screen is normally handed a number by the sign-in screen and asks for
 * four fields and no fifth. It is not always handed one: the link is pressed on an
 * untouched form, or the address is typed, and then the screen has nobody to recover.
 * It used to answer that by redirecting to `/login` without a word, which made the
 * route unreachable by the ordinary way into it — the link exists precisely for the
 * operator who has not typed their number yet.
 *
 * So the number is **asked for** rather than assumed, and only where it is missing. It
 * is the same field the sign-in screen validates, through the same shared shape, so one
 * keyboard and one set of accepted spellings serve both.
 */
export const recoverIdentitySchema = z.object({ phone: phoneField });

export interface RecoverIdentityFields {
  phone: string;
}

export type RecoverIdentityValues = z.output<typeof recoverIdentitySchema>;

export type RecoverValues = z.output<ReturnType<typeof buildRecoverSchema>>;
